import "./AddEditCourse.css";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createCourse, updateCourse } from "../../../actions/courses";
import { useNavigate } from "react-router-dom";

const EMPTY_FORM = {
  title: "",
  subtitle: "",
  duration: "",
  courseDetailsShort: "",
  courseDetailsLong: "",
  tags: [],
  courseImage: "",
  prerequisites: [],
  priority: 1,
  videoURL: "",
};

export default function AddEditCourse({ currentId = null, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("profile"));

  const course = useSelector((state) => {
    if (!currentId) return null;
    const fromList = state.CoursesReducer.courses.find(
      (p) => p._id === currentId,
    );
    if (fromList) return fromList;
    return state.CoursesReducer.course?._id === currentId
      ? state.CoursesReducer.course
      : null;
  });

  const [courseData, setCourseData] = useState(EMPTY_FORM);
  const [isDirty, setIsDirty] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (course) setCourseData(course);
  }, [course]);

  const set = (field, value) => {
    setCourseData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCourseData((prev) => ({ ...prev, courseImage: reader.result }));
      setIsDirty(true);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (currentId) {
      dispatch(
        updateCourse(currentId, { ...courseData, name: user?.result?.name }),
      );
    } else {
      const placeholder = {
        title: "Placeholder",
        summary: "Add a new chapter and then delete this placeholder",
        label: "dummmy:chap:dummy",
        contentId: "68877b70afe52e24a346ed50",
        videoURL: "",
      };
      dispatch(
        createCourse(
          {
            ...courseData,
            courseContentList: [placeholder],
            name: user?.result?.name,
          },
          navigate,
        ),
      );
    }
    setIsDirty(false);
    onClose();
  };

  const handleClose = () => {
    if (isDirty && !window.confirm("You have unsaved changes. Close anyway?"))
      return;
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  if (!user?.result?.name) return null;

  return (
    <div className="ace-overlay" onClick={handleOverlayClick}>
      <div className="ace-panel">
        <div className="ace-header">
          <h2 className="ace-title">
            {currentId ? "Edit Course" : "Create Course"}
          </h2>
          <button className="ace-close" type="button" onClick={handleClose}>
            ✕
          </button>
        </div>

        <form
          className="ace-form"
          onSubmit={handleSubmit}
          autoComplete="off"
          noValidate
        >
          <label className="ace-label">
            Title
            <input
              className="ace-input"
              value={courseData.title}
              onChange={(e) => set("title", e.target.value)}
            />
          </label>

          <label className="ace-label">
            Subtitle
            <input
              className="ace-input"
              value={courseData.subtitle || ""}
              onChange={(e) => set("subtitle", e.target.value)}
            />
          </label>

          <label className="ace-label">
            Duration
            <input
              className="ace-input"
              value={courseData.duration || ""}
              onChange={(e) => set("duration", e.target.value)}
            />
          </label>

          <label className="ace-label">
            Short Description (max 150 chars)
            <textarea
              className="ace-textarea"
              rows={3}
              maxLength={150}
              value={courseData.courseDetailsShort || ""}
              onChange={(e) => set("courseDetailsShort", e.target.value)}
            />
          </label>

          <label className="ace-label">
            Full Description (max 1500 chars)
            <textarea
              className="ace-textarea"
              rows={8}
              maxLength={1500}
              value={courseData.courseDetailsLong || ""}
              onChange={(e) => set("courseDetailsLong", e.target.value)}
            />
          </label>

          <label className="ace-label">
            Tags (uppercase, semicolon separated)
            <input
              className="ace-input"
              value={courseData.tags?.join(";") || ""}
              onChange={(e) => set("tags", e.target.value.split(";"))}
            />
          </label>

          <label className="ace-label">
            Prerequisites (titles, semicolon separated)
            <input
              className="ace-input"
              value={courseData.prerequisites?.join(";") || ""}
              onChange={(e) => set("prerequisites", e.target.value.split(";"))}
            />
          </label>

          <label className="ace-label">
            Priority (smallest comes first)
            <input
              className="ace-input ace-input--sm"
              type="number"
              value={courseData.priority ?? 1}
              onChange={(e) => set("priority", e.target.value)}
            />
          </label>

          <label className="ace-label">
            Course URL
            <input
              className="ace-input"
              value={courseData.videoURL || ""}
              onChange={(e) => set("videoURL", e.target.value)}
            />
          </label>

          <label className="ace-label">
            Course Image
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </label>

          {courseData.courseImage && (
            <img
              className="ace-image-preview"
              src={courseData.courseImage}
              alt="Course preview"
            />
          )}

          <div className="ace-actions">
            <button
              className="ace-btn ace-btn--cancel"
              type="button"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button className="ace-btn ace-btn--submit" type="submit">
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
